import { API_ENDPOINTS } from '../constants/api';
import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { TIMEOUT_CONFIG } from '../constants/common';

class SubscriptionService {
  /**
   * Get subscription bundles
   * @returns {Promise<Object>} Bundles response
   */
  async getBundles() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.BUNDLES);
      return ResponseHandler.handleSuccess(response, 'Bundles fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch bundles');
    }
  }

  /**
   * Create a subscription for a creator
   * @param {string} creatorId - Creator ID to subscribe to
   * @param {Object} subscriptionData - Subscription data (bundle ID, returnUrl, etc.)
   * @returns {Promise<Object>} Create subscription response
   */
  async createSubscription(creatorId, subscriptionData) {
    try {
      const response = await apiClient.post(
        `${API_ENDPOINTS.CONTENT_CREATOR}/${creatorId}/subscriptions`,
        subscriptionData,
        { timeout: TIMEOUT_CONFIG.SUBSCRIPTION }
      );
      return ResponseHandler.handleSuccess(response, 'Subscription created successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to create subscription');
    }
  }

  /**
   * Check subscription status for a creator
   * @param {string} creatorId - Creator ID to check subscription status for
   * @returns {Promise<Object>} Subscription check response
   */
  async checkSubscription(creatorId) {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.CONTENT_CREATOR}/${creatorId}/subscription-check`);
      return ResponseHandler.handleSuccess(response, 'Subscription check completed successfully');
    } catch (error) {
      // 404 or other errors mean not subscribed, which is expected
      // Return a success response with subscribed: false
      if (error.response?.status === 404 || !error.response) {
        return {
          success: true,
          status: 404,
          data: { subscribed: false },
          message: 'Not subscribed'
        };
      }
      throw ResponseHandler.handleError(error, 'Failed to check subscription');
    }
  }

  /**
   * Unsubscribe from a creator
   * @param {string} creatorId - Creator ID to unsubscribe from
   * @param {boolean} isManualRemove - Whether to manually remove the subscription (true) or cancel the subscription (false)
   * @returns {Promise<Object>} Unsubscribe response
   */
  async unsubscribe(creatorId, isManualRemove = false) {
    try {
      const response = await apiClient.delete(`${API_ENDPOINTS.CONTENT_CREATOR}/${creatorId}/subscriptions/${isManualRemove}`);
      return ResponseHandler.handleSuccess(response, 'Unsubscribed successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to unsubscribe');
    }
  }
}

export default new SubscriptionService();

